import type { AppliedProductFilters, ProductFilterDef } from '../types/product-metadata.types';

/**
 * Immutably drop a key from the applied-filters map. Kept as a helper so the
 * call sites don't need to use dynamic `delete` (banned by the project's
 * ESLint config) or underscore-prefixed discards.
 */
export function omitAppliedFilter(
  filters: AppliedProductFilters,
  key: string,
): AppliedProductFilters {
  const next: AppliedProductFilters = {};
  for (const [k, v] of Object.entries(filters)) {
    if (k !== key) next[k] = v;
  }
  return next;
}

/** True when an applied filter value selects nothing (missing, blank, or an empty list). */
export function isEmptyFilterValue(value: AppliedProductFilters[string] | undefined): boolean {
  if (value === undefined || value === null) return true;
  if (Array.isArray(value)) return value.length === 0;
  return typeof value === 'string' && value.trim() === '';
}

/**
 * Order-insensitive equality of two applied-filter maps, with empty values treated
 * as absent. Lets "Reset to defaults" disable itself once the scope already matches.
 */
export function areAppliedFiltersEqual(
  a: AppliedProductFilters,
  b: AppliedProductFilters,
): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    const left = a[key];
    const right = b[key];
    const leftEmpty = isEmptyFilterValue(left);
    if (leftEmpty || isEmptyFilterValue(right)) {
      if (leftEmpty !== isEmptyFilterValue(right)) return false;
      continue;
    }
    if (Array.isArray(left) && Array.isArray(right)) {
      const rightValues = new Set(right);
      if (left.length !== right.length || !left.every((v) => rightValues.has(v))) return false;
      continue;
    }
    if (left !== right) return false;
  }
  return true;
}

/**
 * True when `option` is the only value selected in a locked (default) filter.
 * Unticking it would empty a filter that must always keep a value, so the
 * checkbox is disabled instead of letting an apply be reverted afterwards.
 */
export function isLockedLastValue(
  lockedKeys: ReadonlySet<string> | undefined,
  filters: AppliedProductFilters,
  key: string,
  option: string,
): boolean {
  if (!lockedKeys?.has(key)) return false;
  const value = filters[key];
  return Array.isArray(value) && value.length === 1 && value[0] === option;
}

/**
 * Merge two applied-filter maps for the product-list API. Used by step 3 to
 * layer its refinement filters on top of step 2's locked selection.
 *
 * Per-key, the `additional` map always wins — including for multi-select
 * arrays — because step 3 is conceptually the user's most recent intent. Keys
 * absent from `additional` keep `base`'s value untouched.
 *
 * Note: the previous behaviour unioned arrays, which made step-3 array
 * refinements (e.g. brand) effectively no-ops because they could only widen
 * step 2's set, never narrow it.
 */
export function mergeAppliedFilters(
  base: AppliedProductFilters,
  additional: AppliedProductFilters,
): AppliedProductFilters {
  const merged: AppliedProductFilters = { ...base };
  for (const [key, value] of Object.entries(additional)) {
    merged[key] = value;
  }
  return merged;
}

/**
 * Wire format the backend uses for filter maps inside `scopingFilters` and
 * `groupingFilter.filters` on the experiment draft — every value is a string.
 * Multi-select arrays travel as comma-joined strings.
 */
export type SerializedAppliedFilters = Record<string, string>;

/**
 * Serialize the rich UI filter map into the flat-string wire shape the draft
 * API expects. `null` entries are dropped. Arrays are comma-joined; scalars
 * and booleans are stringified. Empty arrays become an empty string so the
 * key still round-trips.
 */
export function serializeAppliedFilters(filters: AppliedProductFilters): SerializedAppliedFilters {
  const out: SerializedAppliedFilters = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === null) continue;
    if (Array.isArray(value)) {
      out[key] = value.join(',');
    } else {
      out[key] = String(value);
    }
  }
  return out;
}

/**
 * Reverse of `serializeAppliedFilters` — used when resuming a draft to
 * rehydrate the UI store from the API response. Filter-def metadata is
 * needed to disambiguate the type of each value (the wire is stringly
 * typed). Without defs we fall back to a comma-detection heuristic so the
 * function is safe to call before metadata loads.
 */
export function deserializeAppliedFilters(
  filters: SerializedAppliedFilters | null | undefined,
  defs: ProductFilterDef[] | undefined,
): AppliedProductFilters {
  if (!filters) return {};
  const out: AppliedProductFilters = {};
  const defByKey = new Map((defs ?? []).map((d) => [d.key, d]));
  for (const [key, value] of Object.entries(filters)) {
    const type = defByKey.get(key)?.type;
    if (type === 'boolean') {
      out[key] = value === 'true';
      continue;
    }
    if (type === 'text_contains') {
      out[key] = value;
      continue;
    }
    if (type === 'multi_select') {
      out[key] = value === '' ? [] : value.split(',');
      continue;
    }
    // Unknown type: heuristic — comma in the value implies an array.
    out[key] = value.includes(',') ? value.split(',') : value;
  }
  return out;
}
